import Link from "next/link"
import { ArrowRight } from "lucide-react"
import type { CareTaskPillar } from "@/data/care-task-pillars"
import { CARE_TASK_ILLUSTRATIONS, DefaultCareTaskIllustration } from "./care-task-illustrations"

interface CareTaskLandingCardProps {
  pillar: CareTaskPillar
  shortDescription: string
}

const LABELS: Record<string, string> = {
  fertilizing: "Fertilizing",
  watering: "Watering",
  mowing: "Mowing",
  "weed-control": "Weed Control",
}

// Green tint ties the care-task line-art to the green-tinted card header,
// echoing how the buying-guide cards tint their equipment art slate.
const ILLUSTRATION_COLOR = "#16a34a" // green-600

export function CareTaskLandingCard({ pillar, shortDescription }: CareTaskLandingCardProps) {
  const Illustration = CARE_TASK_ILLUSTRATIONS[pillar.slug] ?? DefaultCareTaskIllustration
  const shortLabel = LABELS[pillar.slug] ?? pillar.slug

  return (
    <Link
      href={`/guides/${pillar.slug}`}
      data-module="guides_landing_card"
      className="group flex flex-col bg-white rounded-2xl border border-gray-200 hover:border-gray-300 shadow-xs hover:shadow-xl hover:-translate-y-0.5 transition-base overflow-hidden h-full"
    >
      <div className="relative h-28 sm:h-32 border-b border-gray-100 overflow-hidden bg-linear-to-b from-green-50 to-white">
        <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-xs border text-[10.5px] font-bold uppercase tracking-wider bg-white/85 border-green-200 text-green-800">
          Care Task
        </span>
        <div className="absolute inset-0 pt-3 px-6">
          <Illustration color={ILLUSTRATION_COLOR} />
        </div>
      </div>

      <div className="flex-1 flex flex-col px-5 pt-4 pb-4 gap-2">
        <h3 className="text-lg font-bold text-gray-900 leading-tight tracking-tight">
          {shortLabel}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          {shortDescription}
        </p>
      </div>

      <div className="mx-5 mb-5">
        <span className="w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-green-600 group-hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors group-hover:shadow-lg group-hover:shadow-green-600/25">
          <span>Read guide</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  )
}
